import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { authAPI } from '../api';

const NAV_LINKS = [
  { to: '/',             label: 'Home',            testId: 'nav-home' },
  { to: '/gallery',      label: 'Gallery',         testId: 'nav-gallery' },
  { to: '/about',        label: 'About',           testId: 'nav-about' },
  { to: '/meetourteam',  label: 'The Crew',        testId: 'nav-team' },
  { to: '/cdl',          label: 'CDL Scheduling',  testId: 'nav-cdl' },
  { to: '/contact',      label: 'Join Us',         testId: 'nav-contact', hiring: true },
];

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authUrl, setAuthUrl]       = useState('');
  const [menuOpen, setMenuOpen]     = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('gty_token');
    setIsLoggedIn(!!token);
    if (!token) {
      authAPI.getDiscordAuthUrl().then(r => setAuthUrl(r.data.url)).catch(console.error);
    }
  }, [location]);

  // Close mobile menu on route change
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const handleLogin  = () => { if (authUrl) window.location.href = authUrl; };
  const handleLogout = () => {
    localStorage.removeItem('gty_token');
    setIsLoggedIn(false);
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  const linkClass = (path) =>
    `text-xs font-bold uppercase tracking-widest transition-colors px-3 py-1.5 border-2 ${
      isActive(path)
        ? 'border-purple-600 text-purple-400'
        : 'border-transparent text-white hover:border-purple-600 hover:text-purple-400'
    }`;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0c] border-b-4 border-purple-600">
      <div className="max-w-7xl mx-auto px-5 md:px-12 py-2">
        <div className="flex items-center justify-between gap-4">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="header-logo">
            <img src="/logo.png" alt="GTY Logo" className="h-10 md:h-12 w-auto" />
            <div className="flex flex-col">
              <span className="text-xs md:text-sm font-bold tracking-wider uppercase text-white whitespace-nowrap">
                GO TRUCK YOURSELF
              </span>
              <span className="text-[0.45rem] md:text-[0.5rem] text-purple-400 tracking-widest uppercase">
                Trucking Company
              </span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-6">
            {NAV_LINKS.map(l => (
              <Link
                key={l.to}
                to={l.to}
                className={`${linkClass(l.to)} ${l.hiring ? 'flex items-center gap-2' : ''}`}
                data-testid={l.testId}
              >
                {l.label}
                {l.hiring && (
                  <motion.span
                    animate={{ opacity: [1, 0.2, 1] }}
                    transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
                    className="inline-flex items-center px-1.5 py-0.5 bg-[#7c3aed] text-white text-[8px] font-black uppercase tracking-wider rounded-sm leading-none"
                  >
                    Hiring
                  </motion.span>
                )}
              </Link>
            ))}
            {isLoggedIn && (
              <Link to="/dashboard" className={linkClass('/dashboard')} data-testid="nav-dashboard">
                Dashboard
              </Link>
            )}
          </nav>

          {/* Desktop auth button */}
          <div className="hidden md:block flex-shrink-0">
            {isLoggedIn ? (
              <button onClick={handleLogout} className="btn-secondary text-xs px-5 py-2" data-testid="logout-button">
                Logout
              </button>
            ) : (
              <button onClick={handleLogin} className="btn-primary text-xs px-5 py-2" data-testid="login-button">
                Login
              </button>
            )}
          </div>

          {/* Mobile: auth + hamburger */}
          <div className="flex md:hidden items-center gap-3 flex-shrink-0">
            {isLoggedIn ? (
              <button onClick={handleLogout} className="text-xs font-black uppercase tracking-widest text-[#a1a1aa] border border-[#27272a] px-3 py-1.5" data-testid="logout-button">
                Logout
              </button>
            ) : (
              <button onClick={handleLogin} className="text-xs font-black uppercase tracking-widest text-white bg-[#7c3aed] px-3 py-1.5" data-testid="login-button">
                Login
              </button>
            )}
            <button
              onClick={() => setMenuOpen(o => !o)}
              className="text-white p-1.5 border-2 border-[#27272a] hover:border-purple-600 transition-colors"
              aria-label="Toggle menu"
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile dropdown menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden overflow-hidden border-t-2 border-[#27272a] bg-[#0a0a0c]"
          >
            <nav className="flex flex-col divide-y divide-[#141417]">
              {NAV_LINKS.map(l => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-${l.testId}`}
                  className={`px-5 py-4 text-xs font-black uppercase tracking-widest flex items-center justify-between ${
                    isActive(l.to) ? 'text-purple-400 bg-[#7c3aed]/5' : 'text-white'
                  }`}
                >
                  {l.label}
                  {l.hiring && (
                    <span className="inline-flex items-center px-2 py-0.5 bg-[#7c3aed] text-white text-[8px] font-black uppercase tracking-wider leading-none">
                      Hiring
                    </span>
                  )}
                  {isActive(l.to) && (
                    <span className="w-1.5 h-1.5 bg-purple-500 rounded-full" />
                  )}
                </Link>
              ))}
              {isLoggedIn && (
                <Link
                  to="/dashboard"
                  data-testid="mobile-nav-dashboard"
                  className={`px-5 py-4 text-xs font-black uppercase tracking-widest flex items-center justify-between ${
                    isActive('/dashboard') ? 'text-purple-400 bg-[#7c3aed]/5' : 'text-white'
                  }`}
                >
                  Dashboard
                  {isActive('/dashboard') && <span className="w-1.5 h-1.5 bg-purple-500 rounded-full" />}
                </Link>
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
